import React, { useState, useEffect, useRef } from "react";
import MultiSelectDropdown from "../../../components/ui/MultiSelectDropdown";
import { makeRequest } from "../../../services/api/client";
import { useOcelMetadata } from "../../../stores/OcelContext";
import DateRangePresets from "../../filter/TabFilterModal/DateRangePresets";

// Preset time scope IDs
const PRESETS = [
  { id: "all", label: "All time" },
  { id: "today", label: "Today" },
  { id: "yesterday", label: "Yesterday" },
  { id: "last7days", label: "Last 7 days" },
  { id: "custom", label: "Custom" },
];

const computePreset = (id) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayEnd = new Date();
  todayEnd.setHours(23, 59, 59, 999);

  if (id === "all") return { enabled: false, startDate: null, endDate: null };

  if (id === "today") {
    return {
      enabled: true,
      startDate: today.toISOString(),
      endDate: todayEnd.toISOString(),
    };
  }

  if (id === "yesterday") {
    const yStart = new Date(today);
    yStart.setDate(yStart.getDate() - 1);
    const yEnd = new Date(todayEnd);
    yEnd.setDate(yEnd.getDate() - 1);
    return {
      enabled: true,
      startDate: yStart.toISOString(),
      endDate: yEnd.toISOString(),
    };
  }

  if (id === "last7days") {
    const weekStart = new Date(today);
    weekStart.setDate(weekStart.getDate() - 6);
    return {
      enabled: true,
      startDate: weekStart.toISOString(),
      endDate: todayEnd.toISOString(),
    };
  }

  return null; // custom
};

const detectPreset = (dateRange) => {
  if (!dateRange?.enabled) return "all";

  const now = new Date();
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const todayEnd = new Date(now);
  todayEnd.setHours(23, 59, 59, 999);

  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayEnd = new Date(todayEnd);
  yesterdayEnd.setDate(yesterdayEnd.getDate() - 1);

  const last7 = new Date(today);
  last7.setDate(last7.getDate() - 6);

  const startMs = dateRange.startDate
    ? new Date(dateRange.startDate).getTime()
    : null;
  const endMs = dateRange.endDate
    ? new Date(dateRange.endDate).getTime()
    : null;

  const matches = (a, b) => a !== null && b !== null && Math.abs(a - b) < 60000;

  if (matches(startMs, today.getTime()) && matches(endMs, todayEnd.getTime()))
    return "today";
  if (
    matches(startMs, yesterday.getTime()) &&
    matches(endMs, yesterdayEnd.getTime())
  )
    return "yesterday";
  if (matches(startMs, last7.getTime()) && matches(endMs, todayEnd.getTime()))
    return "last7days";

  return "custom";
};

const ObjectTableSettings = ({
  componentTitle,
  onTitleChange,
  objectType,
  onObjectTypeChange,
  selectedColumns,
  onSelectedColumnsChange,
  availableObjectTypes,
  availableAttributes,   // fallback when no object type selected
  availableActivities,
  localFilters,
  updateLocalFilter,
  activeFile,
  loading,
}) => {
  const metadata = useOcelMetadata(activeFile);
  const datasetMin = metadata?.date_range?.start
    ? metadata.date_range.start.split("T")[0]
    : undefined;
  const datasetMax = metadata?.date_range?.end
    ? metadata.date_range.end.split("T")[0]
    : undefined;

  const [timeScopePreset, setTimeScopePreset] = useState(() =>
    detectPreset(localFilters?.dateRange)
  );

  // Attributes scoped to the selected object type
  const [scopedAttributes, setScopedAttributes] = useState(null); // null = not yet fetched
  const [attrsLoading, setAttrsLoading] = useState(false);

  // Local draft state for custom date inputs — decoupled from parent to avoid
  // React re-renders resetting Chrome's internal date-section cursor while typing
  const [draftStart, setDraftStart] = useState(
    () => (localFilters?.dateRange?.startDate || "").slice(0, 10)
  );
  const [draftEnd, setDraftEnd] = useState(
    () => (localFilters?.dateRange?.endDate || "").slice(0, 10)
  );
  const startRef = useRef(null);
  const endRef = useRef(null);

  // Native 'change' fires when the calendar picker closes (day selected) and on blur
  // after manual keyboard editing — but NOT on each spinner increment click.
  useEffect(() => {
    const el = startRef.current;
    if (!el) return;
    const handle = () => {
      const val = el.value;
      setDraftStart(val);
      updateLocalFilter("dateRange", {
        ...localFilters?.dateRange,
        enabled: true,
        startDate: val || null,
      });
    };
    el.addEventListener('change', handle);
    return () => el.removeEventListener('change', handle);
  }, [localFilters?.dateRange, updateLocalFilter]);

  useEffect(() => {
    const el = endRef.current;
    if (!el) return;
    const handle = () => {
      const val = el.value;
      setDraftEnd(val);
      updateLocalFilter("dateRange", {
        ...localFilters?.dateRange,
        enabled: true,
        endDate: val || null,
      });
    };
    el.addEventListener('change', handle);
    return () => el.removeEventListener('change', handle);
  }, [localFilters?.dateRange, updateLocalFilter]);

  // Sync preset detection when modal re-opens with different localFilters
  useEffect(() => {
    setTimeScopePreset(detectPreset(localFilters?.dateRange));
  }, []); // only on mount

  // Fetch attributes for the current objectType on mount
  useEffect(() => {
    if (activeFile) {
      fetchAttributesForType(objectType || undefined);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAttributesForType = async (type) => {
    if (!activeFile) return;
    setAttrsLoading(true);
    try {
      const data = await makeRequest("/ocel/object-type-attributes", {
        params: { file_type: activeFile, object_type: type || undefined },
      }, "object type attributes");
      setScopedAttributes(data?.attributes || []);
    } catch {
      setScopedAttributes(null);
    } finally {
      setAttrsLoading(false);
    }
  };

  // The attribute list shown in the columns dropdown
  const attributeOptions = scopedAttributes !== null ? scopedAttributes : availableAttributes;

  const handleObjectTypeChange = (val) => {
    onObjectTypeChange(val);
    updateLocalFilter("objectTypes", val ? [val] : []);
    onSelectedColumnsChange([]); // reset columns — new type has different attributes
    setScopedAttributes(null); // clear while loading
    fetchAttributesForType(val || undefined);
  };

  const handlePresetClick = (presetId) => {
    setTimeScopePreset(presetId);
    if (presetId !== "custom") {
      updateLocalFilter("dateRange", computePreset(presetId));
      setDraftStart("");
      setDraftEnd("");
    } else {
      // Keep existing dateRange but mark enabled; seed drafts from current values
      updateLocalFilter("dateRange", {
        ...localFilters?.dateRange,
        enabled: true,
      });
      setDraftStart(toDateInputVal(localFilters?.dateRange?.startDate));
      setDraftEnd(toDateInputVal(localFilters?.dateRange?.endDate));
    }
  };

  const handleDateRangePresetChange = (value) => {
    updateLocalFilter("dateRange", value);
    setTimeScopePreset("custom");
    setDraftStart(toDateInputVal(value?.startDate));
    setDraftEnd(toDateInputVal(value?.endDate));
  };

  const included = localFilters?.activityFilters?.included || [];
  const excluded = localFilters?.activityFilters?.excluded || [];

  const handleIncludedChange = (vals) => {
    updateLocalFilter("activityFilters", {
      included: vals,
      excluded,
      enabled: vals.length > 0 || excluded.length > 0,
    });
  };

  const handleExcludedChange = (vals) => {
    updateLocalFilter("activityFilters", {
      included,
      excluded: vals,
      enabled: included.length > 0 || vals.length > 0,
    });
  };

  const toDateInputVal = (isoStr) => {
    if (!isoStr) return "";
    return isoStr.slice(0, 10);
  };

  return (
    <div className="space-y-5">
      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Component Title{" "}
          <span className="text-gray-400 font-normal">(optional)</span>
        </label>
        <input
          type="text"
          value={componentTitle}
          onChange={(e) => onTitleChange(e.target.value)}
          placeholder="Object Table"
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Object Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Object Type
        </label>
        <select
          value={objectType}
          onChange={(e) => handleObjectTypeChange(e.target.value)}
          disabled={loading || availableObjectTypes.length === 0}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white disabled:bg-gray-50 disabled:text-gray-400"
        >
          <option value="">All object types</option>
          {availableObjectTypes.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <p className="text-xs text-gray-500 mt-1">
          Scope rows to a specific object type
        </p>
      </div>

      {/* Column selection */}
      <div>
        <MultiSelectDropdown
          label="Attribute Columns"
          options={attributeOptions}
          selectedValues={selectedColumns}
          onSelectionChange={onSelectedColumnsChange}
          placeholder="Select attribute columns..."
          disabled={loading || attrsLoading || attributeOptions.length === 0}
          itemType="columns"
        />
        {attrsLoading && (
          <p className="text-xs text-gray-400 mt-1">Loading attributes…</p>
        )}
        {!attrsLoading && attributeOptions.length === 0 && (
          <p className="text-xs text-amber-600 mt-1">
            No attributes available — upload an OCEL file first
          </p>
        )}

        {/* Column order */}
        {selectedColumns.length > 0 && (
          <div className="mt-2">
            <p className="text-xs text-gray-500 mb-1">Column order (drag to reorder):</p>
            <ol className="space-y-1">
              {selectedColumns.map((col, idx) => (
                <li
                  key={col}
                  className="flex items-center gap-1 bg-gray-50 border border-gray-200 rounded px-2 py-1 text-xs text-gray-700"
                >
                  <span className="text-gray-400 w-4 text-right flex-shrink-0">{idx + 1}.</span>
                  <span className="flex-1 truncate ml-1" title={col}>{col}</span>
                  <button
                    type="button"
                    disabled={idx === 0}
                    onClick={() => {
                      const next = [...selectedColumns];
                      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
                      onSelectedColumnsChange(next);
                    }}
                    className="p-0.5 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Move up"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    disabled={idx === selectedColumns.length - 1}
                    onClick={() => {
                      const next = [...selectedColumns];
                      [next[idx + 1], next[idx]] = [next[idx], next[idx + 1]];
                      onSelectedColumnsChange(next);
                    }}
                    className="p-0.5 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Move down"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>

      {/* Time Scope */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Time Scope
          {datasetMin && datasetMax && (
            <span className="text-xs text-gray-400 font-normal ml-2">
              ({datasetMin} – {datasetMax})
            </span>
          )}
        </label>
        <div className="flex flex-wrap gap-2 mb-3">
          {PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => handlePresetClick(p.id)}
              className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
                timeScopePreset === p.id
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        <DateRangePresets value={localFilters?.dateRange} onChange={handleDateRangePresetChange} />

        {timeScopePreset === "custom" && (
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">From</label>
              <input
                ref={startRef}
                type="date"
                value={draftStart}
                onChange={(e) => setDraftStart(e.target.value)}
                min={datasetMin}
                max={datasetMax}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">To</label>
              <input
                ref={endRef}
                type="date"
                value={draftEnd}
                onChange={(e) => setDraftEnd(e.target.value)}
                min={datasetMin}
                max={datasetMax}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        )}
      </div>

      {/* Activity Filter */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Activity Filter
        </label>
        <p className="text-xs text-gray-500 mb-3">
          Filter objects by the activities that occurred in their lifecycle.
          Events must fall within the chosen time scope.
        </p>

        <div className="space-y-3">
          <MultiSelectDropdown
            label="Include only objects with any of:"
            options={availableActivities.filter((a) => !excluded.includes(a))}
            selectedValues={included}
            onSelectionChange={handleIncludedChange}
            placeholder="Select activities to include..."
            disabled={loading}
            itemType="activities"
          />

          <MultiSelectDropdown
            label="Exclude objects that had any of:"
            options={availableActivities.filter((a) => !included.includes(a))}
            selectedValues={excluded}
            onSelectionChange={handleExcludedChange}
            placeholder="Select activities to exclude..."
            disabled={loading}
            itemType="activities"
          />
        </div>
      </div>
    </div>
  );
};

export default ObjectTableSettings;
